// State store du panneau chat LLM. Pattern symetrique a `rmf/tasks.svelte.ts` : singleton
// global, expose des methodes typees, $state reactif pour les composants consommateurs.
//
// Cycle de vie :
//   - mount du ChatPanel : appelle `restore()` qui re-charge l'historique depuis sessionStorage
//   - sendMessage(text) : push optimiste du user message, POST backend, push reply, persist
//   - clear() : appelle au switch-world (cf [map]/+page.svelte) ou logout (cf auth/logout.ts)
//
// Persistance : sessionStorage (cle `rmf:chat:history`). Survit aux F5 mais clear a la fermeture
// de tab ou au switch-world. C'est l'effet voulu pour une demo session (~16 min de heartbeat).

import {
  llmChatClient,
  type LlmChatMessage,
  type LlmDispatchedTask
} from '$lib/api/llm-chat-client';

const STORAGE_KEY = 'rmf:chat:history';

/** Une entree dans l'historique chat. Etend `LlmChatMessage` (role + content) avec des
 *  metadonnees UI : timestamp pour le tri/affichage, `dispatchedTasks` snapshot des tasks
 *  dispatchees pendant ce tour, `tokensIn`/`tokensOut` pour la transparence cost. */
export type ChatMessage = {
  /** Identifiant local pour la cle Svelte `{#each ...}`. Pas le `task_id` RMF. */
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly content: string;
  readonly timestamp: number;
  /** Uniquement sur les messages assistant : les tasks dispatchees pendant ce tour. Sert au
   *  rendu des chips cliquables (zoom cam sur le robot assigne). */
  readonly dispatchedTasks?: readonly LlmDispatchedTask[];
  /** Uniquement sur les messages assistant : tokens consommes pendant ce tour. Cumulables
   *  cote store pour le compteur cost session. */
  readonly tokensIn?: number;
  readonly tokensOut?: number;
};

/** Etat des flags backend (truncation historique, cap dispatch atteint). Affichage UI :
 *  banniere d'avertissement persistente jusqu'a ce qu'on remette a zero (next sendMessage). */
export type ChatFlags = {
  /** Le dernier dispatch a atteint le cap LLM_MAX_DISPATCHES (default 20). */
  readonly dispatchCapReached: boolean;
  /** L'historique envoye a Bedrock a ete tronque (> LLM_MAX_HISTORY_MESSAGES, default 30). */
  readonly historyTruncated: boolean;
};

function createChatStore() {
  // Historique complet de la session. Persiste apres chaque mutation via persistHistory().
  let messages = $state<readonly ChatMessage[]>([]);
  // Etat d'envoi : true pendant le POST en cours. Sert au UI a desactiver le bouton Send +
  // afficher un loader. Empeche aussi les double-clics qui flooderaient le LLM.
  let loading = $state(false);
  // Derniere erreur affichable (texte i18n key ou message brut). Reset au prochain
  // sendMessage reussi.
  let lastError = $state<string | null>(null);
  // Flags backend du dernier tour. Affichage banniere persistante jusqu'au prochain tour.
  let flags = $state<ChatFlags>({ dispatchCapReached: false, historyTruncated: false });

  /** Charge l'historique depuis sessionStorage. Appele au mount du ChatPanel via `restore()`.
   *  Idempotent : si messages deja peuple en memoire, on n'ecrase pas (cas re-mount apres
   *  navigation interne sans F5). */
  function loadFromStorage(): readonly ChatMessage[] {
    if (typeof sessionStorage === 'undefined') return [];
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as ChatMessage[];
      // Defense : si la shape a change (ex schema bump), on ignore plutot que de crash.
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((m) => m && typeof m.id === 'string' && typeof m.content === 'string');
    } catch (err) {
      console.warn('chatStore: failed to restore history, starting fresh', err);
      return [];
    }
  }

  function persistHistory(next: readonly ChatMessage[]): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      // Quota exceeded en pratique impossible (qq KB par message), mais on tolere silencieux.
      console.warn('chatStore: failed to persist history', err);
    }
  }

  /** Identifiant local d'une entree chat. Date.now() + suffix random pour eviter collision
   *  si 2 messages crees a la meme ms (rare mais possible). */
  function newMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /** A appeler au mount du ChatPanel. Restore l'historique persiste si rien en memoire. */
  function restore(): void {
    if (messages.length > 0) return;
    const restored = loadFromStorage();
    if (restored.length > 0) messages = restored;
  }

  /** Envoie un nouveau message user et attend la reponse assistant. Push optimiste + persist
   *  apres reply. En cas d'erreur, le message user reste dans l'historique (pas perdu, l'user
   *  peut retry au prochain tour). Throws : propage l'erreur backend pour que la UI puisse
   *  l'afficher dans un toast. */
  async function sendMessage(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    loading = true;
    lastError = null;

    const userMessage: ChatMessage = {
      id: newMessageId(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now()
    };
    const optimisticHistory = [...messages, userMessage];
    messages = optimisticHistory;
    persistHistory(optimisticHistory);

    // Le payload backend est juste {role, content}, sans nos metadonnees UI. Le mapping se
    // fait ici plutot que dans le client pour garder le client agnostique de notre shape UI.
    const payload: LlmChatMessage[] = optimisticHistory.map((m) => ({
      role: m.role,
      content: m.content
    }));

    try {
      const response = await llmChatClient.sendMessage(payload);
      const assistantMessage: ChatMessage = {
        id: newMessageId(),
        role: 'assistant',
        content: response.message,
        timestamp: Date.now(),
        dispatchedTasks: response.dispatched_tasks,
        tokensIn: response.tokens_in,
        tokensOut: response.tokens_out
      };
      const finalHistory = [...optimisticHistory, assistantMessage];
      messages = finalHistory;
      persistHistory(finalHistory);
      flags = {
        dispatchCapReached: response.dispatch_cap_reached,
        historyTruncated: response.history_truncated
      };
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);
      // On garde le user message dans l'historique : l'utilisateur peut retry en re-soumettant
      // ou en modifiant son message. Le message user n'est PAS retire.
      console.warn('chatStore.sendMessage failed', err);
      throw err;
    } finally {
      loading = false;
    }
  }

  /** Reset complet de l'historique + flags + erreur. Appele au switch-world (cf
   *  [map]/+page.svelte $effect) et au logout (cf auth/logout.ts). Purge aussi sessionStorage
   *  pour eviter qu'un F5 ramene un historique perime. */
  function clear(): void {
    messages = [];
    lastError = null;
    flags = { dispatchCapReached: false, historyTruncated: false };
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /** Reset uniquement le dernier flag d'erreur (apres que l'user a ferme un toast). */
  function clearError(): void {
    lastError = null;
  }

  return {
    get messages() {
      return messages;
    },
    get loading() {
      return loading;
    },
    get lastError() {
      return lastError;
    },
    get flags() {
      return flags;
    },
    /** Somme cumulee des tokens input sur toute la session. Sert au compteur cost UI. */
    get totalTokensIn(): number {
      return messages.reduce((sum, m) => sum + (m.tokensIn ?? 0), 0);
    },
    /** Somme cumulee des tokens output. */
    get totalTokensOut(): number {
      return messages.reduce((sum, m) => sum + (m.tokensOut ?? 0), 0);
    },
    /** Cost approximatif USD. Tarif Claude Haiku 4.5 (cf llm-chat.md § Couts Bedrock) :
     *  $0.25 / 1M input, $1.25 / 1M output. Affiche en UI sous forme "~$0.0023 cette session". */
    get totalCostUsd(): number {
      return (this.totalTokensIn / 1_000_000) * 0.25 + (this.totalTokensOut / 1_000_000) * 1.25;
    },
    restore,
    sendMessage,
    clear,
    clearError
  };
}

export const chatStore = createChatStore();

// Debug helper DEV : expose le store sur window (cf state-store.ts pattern). Permet
// d'inspecter `__chat.messages`, forcer un `__chat.clear()`, etc. depuis la console.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __chat: typeof chatStore }).__chat = chatStore;
}
